import { useRef, useState } from "react";
import { flushSync } from "react-dom";
import { RotateCcw } from "lucide-react";
import { Button } from "@sfc/ui";
import {
  definitionFor,
  FILE_TYPE_PRESETS,
  fileTypePresetOf,
  orderedQuestions,
  orderedSections,
  optionsOf,
  offersOther,
  questionsInFormOrder,
  walkSections,
  type AnswerMap,
  supportsValidator,
  takesAnswer,
  VALIDATION_RULE_LABELS,
  type FormContent,
  type ValidationRule,
} from "@sfc/forms";
import { fileUploadSummary } from "@sfc/forms/react";
import { formatNumber } from "@lib/formatters";
import { FormProse } from "./FormProse";

function describeRule(rule: ValidationRule): string {
  if (rule.kind === "regex") {
    return `${rule.negate ? "Must not match pattern" : "Must match pattern"}: ${rule.pattern}`;
  }
  if (rule.kind === "contains") {
    return `${rule.negate ? "Must not contain" : "Must contain"}: “${rule.value}”`;
  }
  if (!("value" in rule)) return VALIDATION_RULE_LABELS[rule.kind];
  const formatted = formatNumber(rule.value, 20);
  // Bounds must describe the exact enforced value, even below decimal precision.
  const value =
    Number(formatted.replaceAll(",", "")) === rule.value
      ? formatted
      : String(rule.value);
  if (rule.kind === "maxLength") return `${value} characters max`;
  if (rule.kind === "minLength") return `${value} characters min`;
  return `${VALIDATION_RULE_LABELS[rule.kind]}: ${value}`;
}

/** Explore section routes with temporary choices, never saved applicant answers. */
export function FormGuideView({ content }: { content: FormContent }) {
  const [selection, setSelection] = useState<Record<
    string,
    string | null
  > | null>(null);
  const sectionDrivers = new Set(
    content.sections.flatMap((section) => [
      ...(section.visibleWhen ? [section.visibleWhen.questionId] : []),
      ...(section.jumps ?? []).map((jump) => jump.when.questionId),
    ]),
  );
  // A question controlling whether another route choice is asked also steers
  // the section path. Include these dependencies, but not ordinary follow-ups.
  for (const id of sectionDrivers) {
    const question = content.questions.find((candidate) => candidate.id === id);
    if (question?.visibleWhen)
      sectionDrivers.add(question.visibleWhen.questionId);
  }
  const drivers = questionsInFormOrder(content).filter((question) =>
    sectionDrivers.has(question.id),
  );
  const choices = drivers.filter(
    (question) =>
      (question.type === "multiple_choice" || question.type === "dropdown") &&
      optionsOf(question).length > 0 &&
      !offersOther(question),
  );
  // Keep the full document when a route needs an input this compact panel
  // cannot represent (for example a numeric threshold or an uploaded file).
  const canExplore =
    choices.length > 0 && choices.length === sectionDrivers.size;
  const answers: AnswerMap = {};
  for (const question of choices) {
    if (!question.required && selection?.[question.id] === null) continue;
    const options = optionsOf(question);
    const option =
      options.find((option) => option.id === selection?.[question.id]) ??
      options[0]!;
    answers[question.id] = {
      kind: "choice",
      choice: { kind: "option", optionId: option.id },
    };
  }
  const exploring = canExplore && selection !== null;
  const sections = exploring
    ? walkSections(content, answers)
    : orderedSections(content);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [overview, setOverview] = useState(false);
  const sectionNode = useRef<HTMLElement>(null);
  function selectSection(sectionId: string | null) {
    flushSync(() => {
      setOverview(sectionId === null);
      if (sectionId !== null) setActiveSectionId(sectionId);
    });
    sectionNode.current?.scrollIntoView({ block: "start" });
    sectionNode.current?.focus({ preventScroll: true });
  }
  const showingOverview = overview && !!content.description;
  const activeSection =
    sections.find((section) => section.id === activeSectionId) ?? sections[0];
  const hasBranching =
    content.sections.some(
      (section) => section.visibleWhen || section.jumps?.length,
    ) || content.questions.some((question) => question.visibleWhen);

  return (
    <div className="flex min-w-0 flex-col gap-6 break-words">
      {canExplore ? (
        <section
          aria-label="Preview choices"
          className="flex flex-col gap-5 border-b border-border pb-5"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h3 className="heading-sm">Preview your application</h3>
              <p className="body-sm text-muted-foreground">
                {exploring
                  ? "Showing sections for the choices below."
                  : "All sections are available below."}{" "}
                The sections and questions you’ll answer may depend on your
                responses. Try a path without starting an application.
              </p>
            </div>
            <Button
              variant="nav"
              className="body-sm min-h-11 gap-1.5 px-2 font-normal text-muted-foreground underline-offset-4 hover:bg-transparent hover:text-foreground hover:underline"
              onClick={() => setSelection(null)}
              disabled={!exploring}
            >
              <RotateCcw className="size-3.5" aria-hidden="true" />
              Clear selections
            </Button>
          </div>
          {choices.map((question) => (
            <fieldset key={question.id} className="flex min-w-0 flex-col gap-2">
              <legend className="body-md mb-2 font-medium">
                {question.label || "Untitled question"}
              </legend>
              <div className="flex flex-wrap gap-2">
                {!question.required ? (
                  <Button
                    variant={
                      exploring && !answers[question.id] ? "default" : "outline"
                    }
                    aria-pressed={exploring && !answers[question.id]}
                    onClick={() =>
                      setSelection({ ...selection, [question.id]: null })
                    }
                  >
                    Leave unanswered
                  </Button>
                ) : null}
                {optionsOf(question).map((option) => {
                  const answer = answers[question.id];
                  const selected =
                    exploring &&
                    answer?.kind === "choice" &&
                    answer.choice.kind === "option" &&
                    answer.choice.optionId === option.id;
                  return (
                    <Button
                      key={option.id}
                      variant={selected ? "default" : "outline"}
                      aria-pressed={selected}
                      className="h-auto min-h-11 max-w-full whitespace-normal break-words text-left"
                      onClick={() =>
                        setSelection({ ...selection, [question.id]: option.id })
                      }
                    >
                      {option.label || "Untitled option"}
                    </Button>
                  );
                })}
              </div>
            </fieldset>
          ))}
          {exploring && choices.length > 1 ? (
            <p className="body-sm text-muted-foreground">
              Other choices start with their first option. You can change any of
              them above.
            </p>
          ) : null}
        </section>
      ) : null}
      {sections.length ? (
        <div className="grid items-start gap-5 lg:grid-cols-[14rem_minmax(0,1fr)] lg:gap-6">
          <nav
            aria-label="Application guide sections"
            className="flex min-w-0 flex-wrap gap-1 lg:sticky lg:flex-col lg:flex-nowrap lg:top-20 lg:max-h-[calc(100dvh-10rem)] lg:overflow-y-auto"
          >
            {hasBranching && !canExplore && (
              <header className="mb-2 flex basis-full flex-col gap-2 border-b border-border pb-3 lg:basis-auto">
                {!canExplore && hasBranching && (
                  <p className="body-sm text-muted-foreground">
                    {exploring
                      ? "Showing sections for the choices below."
                      : "All sections are available below."}
                    {hasBranching
                      ? " The sections and questions you’ll answer may depend on your responses."
                      : null}
                  </p>
                )}
              </header>
            )}
            {content.description && (
              <Button
                variant="nav"
                aria-current={showingOverview ? "step" : undefined}
                onClick={() => selectSection(null)}
                className="h-auto min-h-11 justify-start rounded-none border-l-2 border-transparent px-3 py-3 text-left aria-[current=step]:border-primary aria-[current=step]:bg-card aria-[current=step]:text-primary"
              >
                Overview
              </Button>
            )}
            {sections.map((section, index) => (
              <Button
                key={section.id}
                variant="nav"
                aria-current={
                  !showingOverview && activeSection?.id === section.id
                    ? "step"
                    : undefined
                }
                onClick={() => selectSection(section.id)}
                className="h-auto min-h-11 justify-start whitespace-normal rounded-none border-l-2 border-transparent px-3 py-3 text-left aria-[current=step]:border-primary aria-[current=step]:bg-card aria-[current=step]:text-primary"
              >
                <span className="shrink-0 text-muted-foreground">
                  {index + 1}.
                </span>
                <span className="min-w-0 break-words">
                  {section.title || "Untitled section"}
                </span>
              </Button>
            ))}
          </nav>
          <div className="flex min-w-0 flex-col gap-6 border-t border-border pt-5 lg:self-stretch lg:border-t-0 lg:border-l lg:pt-0 lg:pl-6">
            {showingOverview && (
              <section
                ref={sectionNode}
                tabIndex={-1}
                className="scroll-mt-20 space-y-5"
                role="region"
                aria-label="Overview"
              >
                <h3 className="application-section-title">Overview</h3>
                <FormProse value={content.description!} className="body-md" />
              </section>
            )}

            {sections
              .filter(
                (section) =>
                  !showingOverview && section.id === activeSection?.id,
              )
              .map((selected) => {
                const questions = orderedQuestions(content, selected.id);
                return (
                  <section
                    key={selected.id}
                    ref={sectionNode}
                    tabIndex={-1}
                    className="min-w-0 scroll-mt-20"
                    role="region"
                    aria-label={selected.title || "Untitled section"}
                  >
                    <header className="flex flex-col gap-2 pb-3">
                      <h3 className="application-section-title break-words">
                        {selected.title || "Untitled section"}
                      </h3>
                      {selected.description ? (
                        <FormProse
                          value={selected.description}
                          className="body-md text-muted-foreground"
                        />
                      ) : null}
                      {selected.visibleWhen ? (
                        <p className="body-sm text-muted-foreground">
                          This section may apply depending on your answers.
                        </p>
                      ) : null}
                      {questions.some(
                        (question) =>
                          takesAnswer(question.type) && question.required,
                      ) ? (
                        <p className="body-sm text-muted-foreground">
                          <span className="text-destructive">*</span> Required
                        </p>
                      ) : null}
                    </header>
                    <div className="flex flex-col divide-y divide-border/60 border-t border-border">
                      {questions.length === 0 ? (
                        <p className="body-md py-4 text-muted-foreground">
                          No questions in this section yet.
                        </p>
                      ) : null}
                      {questions.map((question) => {
                        const group = content.groups?.find(
                          (g) => g.id === question.groupId,
                        );
                        const startsGroup =
                          group &&
                          questions.find((q) => q.groupId === group.id)?.id ===
                            question.id;
                        const answerable = takesAnswer(question.type);
                        const description = question.shortDescription?.trim()
                          ? question.shortDescription
                          : undefined;
                        const isChoice =
                          question.type === "multiple_choice" ||
                          question.type === "dropdown" ||
                          question.type === "checkboxes";
                        const answerChoices = isChoice
                          ? optionsOf(question)
                          : [];
                        const hasOther = offersOther(question);
                        const showType =
                          answerable &&
                          !isChoice &&
                          question.type !== "short_answer" &&
                          question.type !== "paragraph" &&
                          question.type !== "file_upload";
                        const rules = (question.validation ?? []).filter(
                          (rule) => supportsValidator(question.type, rule.kind),
                        );
                        const filePreset = FILE_TYPE_PRESETS.find(
                          (preset) =>
                            preset.key ===
                            fileTypePresetOf(question.config?.acceptedTypes),
                        );
                        return (
                          <article
                            key={question.id}
                            className="flex min-w-0 flex-col gap-2 py-3"
                          >
                            {startsGroup ? (
                              <header className="flex flex-col gap-2 pb-2">
                                <h4 className="heading-sm">{group.title}</h4>
                                {group.description ? (
                                  <FormProse
                                    value={group.description}
                                    className="body-sm text-muted-foreground"
                                  />
                                ) : null}
                                {group.repeat ? (
                                  <p className="body-sm text-muted-foreground">
                                    Provide{" "}
                                    {group.repeat.minEntries > 0
                                      ? `${group.repeat.minEntries}–${group.repeat.maxEntries}`
                                      : `up to ${group.repeat.maxEntries}`}{" "}
                                    {group.repeat.entryLabel?.toLowerCase() ||
                                      "entry"}{" "}
                                    entries. Complete the following fields for
                                    each entry.
                                  </p>
                                ) : null}
                              </header>
                            ) : null}
                            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                              <h4 className="body-md min-w-0 flex-1 basis-full font-medium break-words sm:basis-48">
                                {question.label ||
                                  (answerable
                                    ? "Untitled question"
                                    : "Information")}
                                {answerable && question.required ? (
                                  <>
                                    <span
                                      aria-hidden
                                      className="ml-0.5 text-destructive"
                                    >
                                      *
                                    </span>
                                    <span className="sr-only"> (required)</span>
                                  </>
                                ) : null}
                              </h4>
                              {showType ? (
                                <p className="body-sm text-muted-foreground">
                                  {definitionFor(question.type).label}
                                </p>
                              ) : null}
                            </div>
                            <div className="flex min-w-0 flex-col gap-2 empty:hidden">
                              {isChoice &&
                              (answerChoices.length > 0 || hasOther) ? (
                                <ul
                                  aria-label="Answer choices"
                                  className="body-sm flex list-none flex-col gap-y-2 text-muted-foreground sm:flex-row sm:flex-wrap sm:gap-x-0"
                                >
                                  {answerChoices.map((option) => (
                                    <li
                                      key={option.id}
                                      className="min-w-0 break-words sm:border-l sm:border-border sm:px-3 sm:first:border-l-0 sm:first:pl-0"
                                    >
                                      {option.label}
                                    </li>
                                  ))}
                                  {hasOther ? (
                                    <li className="min-w-0 break-words sm:border-l sm:border-border sm:px-3 sm:first:border-l-0 sm:first:pl-0">
                                      Other (write in)
                                    </li>
                                  ) : null}
                                </ul>
                              ) : null}
                              {isChoice &&
                              answerChoices.length === 0 &&
                              !hasOther ? (
                                <p className="body-sm text-muted-foreground">
                                  No options yet
                                </p>
                              ) : null}
                              {question.type === "checkboxes" ? (
                                <p className="body-sm text-muted-foreground">
                                  Select all that apply.
                                </p>
                              ) : null}
                              {question.visibleWhen ? (
                                <p className="body-sm text-muted-foreground">
                                  This question may apply depending on your
                                  answers.
                                </p>
                              ) : null}
                              {description ? (
                                <p className="body-sm whitespace-pre-wrap break-words text-muted-foreground">
                                  {description}
                                </p>
                              ) : null}
                              {rules.length ? (
                                <ul className="body-sm flex list-none flex-col gap-y-2 text-muted-foreground sm:flex-row sm:flex-wrap sm:gap-x-0">
                                  {rules.map((rule, index) => (
                                    <li
                                      key={`${rule.kind}-${index}`}
                                      className="min-w-0 break-words sm:border-l sm:border-border sm:px-3 sm:first:border-l-0 sm:first:pl-0"
                                    >
                                      {describeRule(rule)}
                                    </li>
                                  ))}
                                </ul>
                              ) : null}
                              {question.help?.trim() ? (
                                <FormProse
                                  value={question.help}
                                  className="body-sm text-muted-foreground"
                                />
                              ) : null}
                              {question.type === "file_upload" ? (
                                <p className="body-sm text-muted-foreground">
                                  {fileUploadSummary(question)} •{" "}
                                  {filePreset?.label ??
                                    question.config?.acceptedTypes?.join(", ")}
                                </p>
                              ) : null}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
          </div>
        </div>
      ) : (
        <p className="body-md text-muted-foreground">
          Add a section to see the application guide.
        </p>
      )}
    </div>
  );
}
